import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.tsx';

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-green via-dark/90 to-dark min-h-[85vh] flex items-center">
        {/* Decorative elements */}
        <div className="absolute top-20 -left-20 w-72 h-72 bg-lime/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-green/15 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl animate-float-delayed" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 animate-slide-up">
              Refresh Your Business.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime to-accent">
                Lime by Lime.
              </span>
            </h1>

            <p className="text-xl text-white/70 mb-10 max-w-2xl leading-relaxed">
              Premium beverages crafted for forward-thinking companies. 
              From energizing your team to impressing your clients — we&apos;ve got a lime for every occasion.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to={user ? '/store' : '/register'}
                className="btn-gradient px-8 py-4 rounded-2xl text-lg font-semibold inline-flex items-center gap-2 active:scale-[0.97] transition-transform group"
              >
                {user ? 'Browse Store' : 'Get Started'}
                <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              {!user && (
                <Link
                  to="/login"
                  className="px-8 py-4 rounded-2xl text-lg font-semibold text-white border border-white/20 hover:bg-white/10 transition-all"
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Beverage Showcase */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-4">
              Our Beverage Lines
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Five distinct lines, each crafted with the Runninglime signature — bold flavors, premium ingredients, lime-green attitude.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 stagger-children">
            <BeverageCard
              emoji="🥤"
              name="Soda"
              description="Classic lime sodas in three refreshing flavors"
              gradient="from-lime/20 to-green/20"
            />
            <BeverageCard
              emoji="⚡"
              name="Energy"
              description="Natural energy with botanicals and caffeine"
              gradient="from-accent/20 to-lime/20"
            />
            <BeverageCard
              emoji="🧠"
              name="Brainstorm"
              description="Nootropic-infused drinks for mental clarity"
              gradient="from-green/20 to-dark/10"
            />
            <BeverageCard
              emoji="💧"
              name="Hydrate"
              description="Premium hydration with vitamins and minerals"
              gradient="from-blue-100 to-green/20"
            />
            <BeverageCard
              emoji="☕"
              name="Cold Brew"
              description="Slow-steeped coffee with a lime twist"
              gradient="from-amber-100 to-dark/10"
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-4">
              Why Businesses Choose Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
            <FeatureCard
              icon="✨"
              title="Premium Quality"
              description="Every beverage crafted with natural ingredients and zero compromise. Lab-tested, taste-approved."
            />
            <FeatureCard
              icon="📦"
              title="Bulk Pricing"
              description="Volume discounts that scale. The more you order, the more you save — up to 25% off at 500+ units."
            />
            <FeatureCard
              icon="🚀"
              title="Fast B2B Delivery"
              description="Dedicated logistics for business orders. Track every shipment from warehouse to your door."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-dark via-dark to-green/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Stock Up?
          </h2>
          <p className="text-white/60 mb-8 text-lg">
            Register your business account and start ordering today. Minimum orders from just 12 units.
          </p>
          <Link
            to={user ? '/store' : '/register'}
            className="btn-gradient px-10 py-4 rounded-2xl text-lg font-semibold inline-block hover-glow active:scale-[0.97] transition-transform"
          >
            {user ? 'Go to Store' : 'Create Business Account'}
          </Link>
        </div>
      </section>
    </div>
  );
}

function BeverageCard({ emoji, name, description, gradient }: {
  emoji: string;
  name: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-white/40`}>
      <div className="text-4xl mb-4">{emoji}</div>
      <h3 className="text-lg font-bold text-dark mb-2">{name}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

function FeatureCard({ icon, title, description }: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="glass rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
      <div className="text-3xl mb-4">{icon}</div>
      <h3 className="text-xl font-bold text-dark mb-3">{title}</h3>
      <p className="text-gray-500 leading-relaxed">{description}</p>
    </div>
  );
}
